import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import ImageSlider from "./ImageSlider";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import { trackWhatsAppClick } from "@/lib/analytics";

export default function HeroSection() {
  const [_, navigate] = useLocation();
  const { t } = useLanguage();

  return (
    <div className="relative h-[93vh] w-full overflow-hidden">
      <div className="absolute inset-0">
        <ImageSlider />
      </div>

      {/* Dark overlay with gradient for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent z-10 pointer-events-none" />

      {/* Content container */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col items-center justify-center gap-6 md:gap-8 px-4 w-full pointer-events-auto"
        >
          <h1 className="text-white text-3xl md:text-5xl lg:text-6xl font-bold text-center drop-shadow-lg max-w-4xl px-2 w-full break-words">
            Tailor-Made Uganda and East Africa Safari Experiences
          </h1>
          <h2 className="text-white text-lg md:text-2xl font-semibold text-center drop-shadow-lg mb-4 md:mb-8 max-w-3xl px-2 w-full break-words">
            Gorilla Trekking, Wildlife Safaris, and Cultural Tours Planned by Local Experts
          </h2>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full px-4">
            <Button
              onClick={() => {
                trackWhatsAppClick('Hero Section');
                const message = encodeURIComponent("Hi, I'm interested in planning a safari with Ecoplace Tours.");
                window.open(`https://wa.me/256780628299?text=${message}`, '_blank');
              }}
              className="w-full sm:w-auto h-14 px-6 md:px-8 text-base font-semibold bg-[#25D366] hover:bg-[#128C7E] text-white shadow-lg hover:shadow-xl rounded-full transition-all duration-300 group"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="h-5 w-5 mr-2 fill-current">
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zM223.9 438.6c-32.6 0-64.6-9.1-92.9-26.2l-6.6-4-69 18 18.4-67.4-4.3-6.9c-18.3-29.2-28-63.3-28-98.4 0-101.4 82-183.9 184-183.9 49.1 0 95.3 19.1 130 53.9 34.7 34.7 53.9 80.9 53.9 130 0 101.4-82.5 183.9-184.1 183.9zm101.5-138.4c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
              </svg>
              Plan My Safari
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const element = document.getElementById('popular-experiences');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate("/destinations");
                }
              }}
              className="w-full sm:w-auto h-14 px-6 md:px-8 text-base font-semibold bg-white/15 backdrop-blur-md border-white/70 text-white hover:bg-white/25 hover:border-white shadow-lg hover:shadow-xl rounded-full transition-all duration-300 group"
            >
              View Packages
              <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}